<template>
  <BaseTemplate app-name="RIS System" page-title="">
    <template #content>
      <div class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200">
        <!-- Main Content -->
        <div class="flex-1 flex overflow-hidden">
          <!-- Content Area -->
          <div class="flex-1 overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div class="flex flex-wrap justify-between items-center gap-4">
                <div class="flex items-center gap-3">
                  <h2 class="text-xl font-medium" :style="{ color: 'var(--clinic-font-color)' }">{{ currentView === 'list' ? 'Gerenciamento de Salas' : 'Cadastro de Sala' }}</h2>
                  <button
                    v-if="currentView === 'form'"
                    class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    @click="goToList"
                    aria-label="Voltar para lista"
                  >
                    <ArrowLeftIcon class="w-5 h-5" />
                  </button>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    v-if="currentView === 'list'"
                    class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                    @click="openNewRoomForm"
                  >
                    <PlusIcon class="w-4 h-4" />
                    <span>Nova Sala</span>
                  </button>
                  <button
                    v-if="currentView === 'form'"
                    class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                    @click="saveRoom"
                    :disabled="!isFormValid"
                  >
                    <SaveIcon class="w-4 h-4" />
                    <span>{{ editMode ? 'Atualizar' : 'Salvar' }} Sala</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Room List View -->
            <div v-if="currentView === 'list'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700 p-4">
                <!-- Search and Filter -->
                <div class="mb-4 flex flex-wrap gap-4">
                  <div class="flex-1 min-w-[240px]">
                    <div class="relative">
                      <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Buscar salas..."
                        class="w-full px-3 py-2 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                  <div class="w-full md:w-auto flex gap-2">
                    <div class="relative">
                      <select
                        v-model="filterStatus"
                        class="h-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                      >
                        <option value="">Todos os status</option>
                        <option value="true">Ativo</option>
                        <option value="false">Inativo</option>
                      </select>
                      <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                    <button
                      @click="resetFilters"
                      class="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <RefreshCwIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <!-- Rooms Table -->
                <div class="overflow-x-auto">
                  <table class="w-full border-collapse">
                    <thead>
                      <tr class="bg-gray-50 dark:bg-gray-900/50 text-left">
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">ID</th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Nome</th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Tipo</th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Monitores</th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Status</th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="filteredRooms.length === 0">
                        <td colspan="7" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                          Nenhuma sala encontrada
                        </td>
                      </tr>
                      <tr v-for="room in filteredRooms" :key="room.id"
                        class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <td class="px-4 py-3 text-sm">{{ room.id }}</td>
                        <td class="px-4 py-3">
                          <div class="flex items-center gap-3">
                            <div class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 bg-blue-500">
                              {{ getRoomInitials(room.name) }}
                            </div>
                            <div class="text-sm font-medium">{{ room.name }}</div>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">{{ room.type }}</td>
                       
                        <td class="px-4 py-3 text-sm">{{ room.monitor_count || 0 }}</td>
                        <td class="px-4 py-3 text-sm">
                          <span :class="['px-2 py-1 rounded text-xs font-medium', 
                            room.active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200']">
                            {{ room.active ? 'Ativo' : 'Inativo' }}
                          </span>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex gap-2">
                            <button
                              @click="editRoom(room)"
                              class="p-1 text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400"
                              title="Editar"
                            >
                              <EditIcon class="w-4 h-4" />
                            </button>
                            <button
                              @click="confirmDeleteRoom(room)"
                              class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                              title="Excluir"
                            >
                              <TrashIcon class="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="mt-4 flex justify-between items-center">
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    Mostrando página {{ page }} de {{ totalPages }} (total {{ total }} salas)
                  </div>
                  <div class="flex items-center gap-4">
                    <label for="pageSize" class="text-sm text-gray-600 dark:text-gray-300">
                      Itens por página:
                    </label>
                    <select
                      id="pageSize"
                      v-model="pageSize"
                      @change="fetchRooms(1)"
                      class="px-2 py-1 border rounded bg-white dark:bg-gray-800 text-sm"
                    >
                      <option :value="10">10</option>
                      <option :value="20">20</option>
                      <option :value="30">30</option>
                    </select>
                    <button
                      class="px-3 py-1 border rounded-md"
                      @click="goPrev"
                      :disabled="page <= 1"
                    >
                      Anterior
                    </button>
                    <button
                      class="px-3 py-1 border rounded-md"
                      @click="goNext"
                      :disabled="page >= totalPages"
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Room Form View with Tabs -->
            <div v-if="currentView === 'form'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
                <!-- Tabs Navigation -->
                <div class="border-b border-gray-200 dark:border-gray-700">
                  <div class="flex overflow-x-auto">
                    <button
                      v-for="tab in tabs"
                      :key="tab.id"
                      @click="activeTab = tab.id"
                      class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none"
                      :style="{
                        color: activeTab === tab.id ? 'var(--clinic-icon-color)' : 'var(--clinic-font-color)',
                        borderBottom: activeTab === tab.id ? '2px solid var(--clinic-icon-color)' : '2px solid transparent'
                      }"
                    >
                      <div class="flex items-center gap-2">
                        <component :is="tab.icon" class="w-4 h-4" />
                        {{ tab.name }}
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Tab Content -->
                <div class="p-6">
                  <!-- Basic Information Tab -->
                  <div v-if="activeTab === 'basic'" class="space-y-4">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <!-- ID da Sala -->
                      <div>
                        <label class="block text-sm font-medium mb-1">ID da Sala</label>
                        <input
                          type="text"
                          v-model="currentRoom.id"
                          placeholder="Gerado automaticamente"
                          disabled
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>

                      <!-- Nome da Sala -->
                      <div class="md:col-span-2">
                        <label for="name" class="block text-sm font-medium mb-1">Nome da Sala*</label>
                        <input
                          id="name"
                          type="text"
                          v-model="currentRoom.name"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <!-- Tipo de Sala -->
                      <div>
                        <label for="type" class="block text-sm font-medium mb-1">Tipo de Sala*</label>
                        <div class="relative">
                          <select
                            id="type"
                            v-model="currentRoom.type"
                            required
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                          >
                            <option value="">Selecione</option>
                            <option value="Exame">Sala de Exame</option>
                            <option value="Consultorio">Consultório</option>
                            <option value="Cirurgia">Centro Cirúrgico</option>
                            <option value="Radiologia">Radiologia</option>
                            <option value="Ultrassom">Ultrassom</option>
                            <option value="Tomografia">Tomografia</option>
                            <option value="Ressonancia">Ressonância</option>
                          </select>
                          <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                          </div>
                        </div>
                      </div>

                      <!-- Status -->
                      <div class="flex items-center pt-6">
                        <input
                          id="active"
                          type="checkbox"
                          v-model="currentRoom.active"
                          class="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <label for="active" class="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Sala Ativa
                        </label>
                      </div>
                    </div>
                </div>


                  <!-- Monitors Assignment Tab -->
                  <div v-if="activeTab === 'monitors'" class="space-y-6">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">Gerenciar Monitores da Sala</h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                      Associe monitores que estão disponíveis nesta sala. Os monitores são gerenciados na tela de "Gerenciamento de Monitores".
                    </p>

                    <div class="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
                      <!-- Monitores Disponíveis -->
                      <div class="flex flex-col gap-2">
                        <label for="monitor-search" class="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Monitores Disponíveis
                        </label>
                        <div class="relative">
                          <input
                            id="monitor-search"
                            type="text"
                            v-model="monitorSearchQuery"
                            placeholder="Buscar monitor..."
                            class="h-10 w-full px-3 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                          <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        </div>
                        <div class="border rounded-md bg-white dark:bg-gray-900 dark:border-gray-700 h-72 overflow-y-auto">
                          <ul v-if="availableMonitors.length > 0">
                            <li
                              v-for="monitor in availableMonitors"
                              :key="monitor.id"
                              @click="toggleMonitorSelection('available', monitor.id)"
                              class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                              :class="{ 'bg-blue-100 dark:bg-blue-900/50': selectedAvailableMonitors.includes(monitor.id) }"
                            >
                              <div class="font-medium">{{ monitor.name }}</div>
                            
                            </li>
                          </ul>
                          <div v-else class="text-center text-sm text-gray-500 p-4">
                            Nenhum monitor disponível ou todos já foram atribuídos.
                          </div>
                        </div>
                      </div>

                      <!-- Botões de Controle -->
                      <div class="flex flex-col gap-2 self-center pt-16">
                        <button 
                          @click="moveSelectedMonitorsToRight" 
                          :disabled="selectedAvailableMonitors.length === 0" 
                          class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" 
                          aria-label="Atribuir monitores selecionados"
                          title="Atribuir monitores selecionados"
                        >
                          &gt;
                        </button>
                        <button 
                          @click="moveAllMonitorsToRight" 
                          :disabled="availableMonitors.length === 0" 
                          class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" 
                          aria-label="Atribuir todos os monitores"
                          title="Atribuir todos os monitores"
                        >
                          &gt;&gt;
                        </button>
                        <button 
                          @click="moveSelectedMonitorsToLeft" 
                          :disabled="selectedAssignedMonitors.length === 0" 
                          class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" 
                          aria-label="Desatribuir monitores selecionados"
                          title="Desatribuir monitores selecionados"
                        >
                          &lt;
                        </button>
                        <button 
                          @click="moveAllMonitorsToLeft" 
                          :disabled="assignedMonitors.length === 0" 
                          class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" 
                          aria-label="Desatribuir todos os monitores"
                          title="Desatribuir todos os monitores"
                        >
                          &lt;&lt;
                        </button>
                      </div>

                      <!-- Monitores Atribuídos -->
                      <div class="flex flex-col gap-2">
                        <label class="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Monitores Atribuídos à Sala
                        </label>
                        <div class="border rounded-md bg-white dark:bg-gray-900 dark:border-gray-700 h-72 mt-[2.5rem] overflow-y-auto">
                          <ul v-if="assignedMonitors.length > 0">
                            <li
                              v-for="monitor in assignedMonitors"
                              :key="monitor.id"
                              @click="toggleMonitorSelection('assigned', monitor.id)"
                              class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                              :class="{ 'bg-blue-100 dark:bg-blue-900/50': selectedAssignedMonitors.includes(monitor.id) }"
                            >
                              <div class="font-medium">{{ monitor.name }}</div>
                              
                            </li>
                          </ul>
                          <div v-else class="text-center text-sm text-gray-500 p-4">
                            Nenhum monitor atribuído a esta sala.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Form Actions -->
                  <div class="mt-8 flex justify-between items-center">
                    <div class="flex gap-2"></div>
                    <div class="flex gap-3">
                      <button
                        class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                        @click="goToList"
                      >
                        Cancelar
                      </button>
                      <button
                        class="clinic-btn text-white rounded-md transition-colors shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                        @click="saveRoom"
                        :disabled="!isFormValid"
                      >
                        {{ editMode ? 'Atualizar' : 'Salvar' }} Sala
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Success Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showSuccessModal"
              @click.self="closeSuccessModal"
            >
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn">
                <div class="p-6 text-center">
                  <div class="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto mb-4">
                    <CheckIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2">{{ editMode ? 'Sala Atualizada' : 'Sala Cadastrada' }}</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6">
                    {{ editMode ? 'A sala foi atualizada com sucesso.' : 'A sala foi cadastrada com sucesso no sistema.' }}
                  </p>
                  <div class="flex justify-center gap-3">
                    <button
                      class="clinic-btn text-white rounded-md transition-colors shadow-sm"
                      @click="closeSuccessModal"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Error Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showErrorModal"
              @click.self="showErrorModal = false"
            >
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div class="p-6 text-center">
                  <div class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mx-auto mb-4 flex items-center justify-center">
                    <AlertTriangleIcon class="w-8 h-8 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 class="text-xl font-medium mb-2 text-red-600">Erro ao salvar</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6">{{ errorMessage }}</p>
                  <button
                    class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md shadow-sm"
                    @click="showErrorModal = false"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showDeleteModal"
              @click.self="showDeleteModal = false"
            >
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn">
                <div class="p-6">
                  <div class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4">
                    <AlertTriangleIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2 text-center">Confirmar Exclusão</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 text-center">
                    Tem certeza que deseja excluir a sala <span class="font-medium">{{ roomToDelete?.name }}</span>?
                    <br />Esta ação não pode ser desfeita.
                  </p>
                  <div class="flex justify-end gap-3">
                    <button
                      class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                      @click="showDeleteModal = false"
                    >
                      Cancelar
                    </button>
                    <button
                      class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors shadow-sm"
                      @click="deleteRoom"
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import BaseTemplate from '../templates/BaseTemplate.vue'
import apiClient from '@/services/authService'
import {
  Plus, ChevronDown, Check, Search, Edit, Trash,
  Save, ArrowLeft, RefreshCw, AlertTriangle,
  User, Monitor
} from 'lucide-vue-next'

// Icons
const PlusIcon = Plus
const ChevronDownIcon = ChevronDown
const CheckIcon = Check
const SearchIcon = Search
const EditIcon = Edit
const TrashIcon = Trash
const SaveIcon = Save
const ArrowLeftIcon = ArrowLeft
const RefreshCwIcon = RefreshCw
const AlertTriangleIcon = AlertTriangle
const UserIcon = User
const MonitorIcon = Monitor

// Estados principais
const currentView = ref('list')
const showSuccessModal = ref(false)
const showErrorModal = ref(false)
const showDeleteModal = ref(false)
const editMode = ref(false)
const roomToDelete = ref(null)
const errorMessage = ref('')

const searchQuery = ref('')
const filterStatus = ref('')
const activeTab = ref('basic')

const total = ref(0)
const page = ref(1)
const pageSize = ref(10)

const totalPages = computed(() => Math.ceil(total.value / pageSize.value))

// Tabs
const tabs = [
  { id: 'basic', icon: UserIcon, name: 'Informações Básicas' },
  { id: 'monitors', icon: MonitorIcon, name: 'Monitores' }
]

// Modelo vazio para sala
const emptyRoom = {
  id: null,
  name: '',
  type: '',
  active: true,
  monitor_ids: []
}

// Estados reativos
const rooms = ref([])
const currentRoom = ref({ ...emptyRoom })

// Estados para monitores
const allMonitors = ref([])
const monitorSearchQuery = ref('')
const selectedAvailableMonitors = ref([])
const selectedAssignedMonitors = ref([])

// Computed para monitores
const availableMonitors = computed(() => {
  if (!currentRoom.value) return []
  const assignedIds = new Set(currentRoom.value.monitor_ids)
  const query = monitorSearchQuery.value.toLowerCase()
  return allMonitors.value.filter(monitor => {
    const isNotAssigned = !assignedIds.has(monitor.id)
    const matchesSearch = (monitor.name || '').toLowerCase().includes(query) ||
                         (monitor.brand || '').toLowerCase().includes(query) ||
                         (monitor.model || '').toLowerCase().includes(query)
    return isNotAssigned && matchesSearch && monitor.active
  })
})

const assignedMonitors = computed(() => {
  if (!currentRoom.value) return []
  const assignedIds = new Set(currentRoom.value.monitor_ids)
  return allMonitors.value.filter(monitor => assignedIds.has(monitor.id))
})

// Buscar dados
async function fetchRooms(p = 1) {
  try {
    const { data } = await apiClient.get('/api/v1/rooms/', {
      params: {
        page: p,
        page_size: pageSize.value,
        search: searchQuery.value || undefined,
        active: filterStatus.value || undefined,
      }
    })
    total.value = data.total
    page.value = data.page
    rooms.value = data.results || []
  } catch (e) {
    console.error('Erro ao buscar salas:', e)
  }
}

async function loadAllMonitors() {
  try {
    const { data } = await apiClient.get('/api/v1/monitors/?page_size=100')
    allMonitors.value = data.results || []
  } catch (err) {
    console.error('Erro ao carregar monitores:', err)
  }
}

// Salvar sala
async function saveRoom() {
  if (!isFormValid.value) return
  
  const payload = {
    name: currentRoom.value.name,
    type: currentRoom.value.type,
    active: currentRoom.value.active,
    monitor_ids: currentRoom.value.monitor_ids
  }

  try {
    if (editMode.value) {
      await apiClient.put(`/api/v1/rooms/${currentRoom.value.id}/`, payload)
    } else {
      await apiClient.post('/api/v1/rooms/', payload)
    }
    showSuccessModal.value = true
  } catch (err) {
    const detail = err.response?.data?.detail || 'Erro ao salvar sala'
    errorMessage.value = detail
    showErrorModal.value = true
  }
}

// Editar sala
async function editRoom(room) {
  try {
    const { data } = await apiClient.get(`/api/v1/rooms/${room.id}/`)
    currentRoom.value = {
      id: data.id,
      name: data.name,
      type: data.type,
      active: data.active,
      monitor_ids: data.monitors ? data.monitors.map(m => m.id) : []
    }
    editMode.value = true
    currentView.value = 'form'
    resetMonitorSelections()
  } catch (err) {
    console.error('Erro ao carregar sala:', err)
  }
}

// Excluir sala
async function deleteRoom() {
  if (!roomToDelete.value) return
  try {
    await apiClient.delete(`/api/v1/rooms/${roomToDelete.value.id}/`)
    rooms.value = rooms.value.filter(r => r.id !== roomToDelete.value.id)
    showDeleteModal.value = false
  } catch (err) {
    console.error('Erro ao excluir sala:', err)
  }
}

// Funções para monitores
function toggleMonitorSelection(type, monitorId) {
  const selectionArray = type === 'available' ? selectedAvailableMonitors : selectedAssignedMonitors
  const index = selectionArray.value.indexOf(monitorId)
  if (index > -1) {
    selectionArray.value.splice(index, 1)
  } else {
    selectionArray.value.push(monitorId)
  }
}

function moveSelectedMonitorsToRight() {
  currentRoom.value.monitor_ids.push(...selectedAvailableMonitors.value)
  selectedAvailableMonitors.value = []
}

function moveAllMonitorsToRight() {
  const idsToMove = availableMonitors.value.map(m => m.id)
  currentRoom.value.monitor_ids.push(...idsToMove)
}

function moveSelectedMonitorsToLeft() {
  currentRoom.value.monitor_ids = currentRoom.value.monitor_ids.filter(
    id => !selectedAssignedMonitors.value.includes(id)
  )
  selectedAssignedMonitors.value = []
}

function moveAllMonitorsToLeft() {
  currentRoom.value.monitor_ids = []
  selectedAssignedMonitors.value = []
}

function resetMonitorSelections() {
  selectedAvailableMonitors.value = []
  selectedAssignedMonitors.value = []
  monitorSearchQuery.value = ''
}

// Computed properties
const filteredRooms = computed(() => {
  let res = [...rooms.value]
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    res = res.filter(r =>
      r.name.toLowerCase().includes(q) ||
      r.type.toLowerCase().includes(q) ||
      String(r.id).includes(q)
    )
  }
  if (filterStatus.value) {
    const isActive = filterStatus.value === 'true'
    res = res.filter(r => r.active === isActive)
  }
  return res
})

const isFormValid = computed(() => (
  currentRoom.value.name.trim() &&
  currentRoom.value.type
))

// Funções auxiliares
function getRoomInitials(name) {
  if (!name) return ''
  return name
    .split(' ')
    .map(w => w[0]?.toUpperCase() || '')
    .join('')
    .substring(0, 2)
}

function openNewRoomForm() {
  currentRoom.value = { ...emptyRoom }
  editMode.value = false
  activeTab.value = 'basic'
  currentView.value = 'form'
  resetMonitorSelections()
}

function goToList() {
  currentView.value = 'list'
}

function confirmDeleteRoom(r) {
  roomToDelete.value = r
  showDeleteModal.value = true
}

function closeSuccessModal() {
  showSuccessModal.value = false
  currentView.value = 'list'
  fetchRooms(page.value)
}

function resetFilters() {
  searchQuery.value = ''
  filterStatus.value = ''
  fetchRooms(1)
}

function goPrev() {
  if (page.value > 1) {
    fetchRooms(page.value - 1)
  }
}

function goNext() {
  if (page.value < totalPages.value) {
    fetchRooms(page.value + 1)
  }
}

// Watchers
watch([searchQuery, filterStatus, pageSize], () => {
  fetchRooms(1)
})

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchRooms(),
    loadAllMonitors()
  ])
})
</script>

<style scoped>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.clinic-btn {
  background-color: var(--clinic-btn-bg);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
}

.clinic-btn:hover:not(:disabled) {
  background-color: var(--clinic-btn-bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.clinic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
